import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { canvasInstructorAPI, instructorAPI } from '../services/api';
import { CanvasCourse } from '../types';
import { toast } from 'react-hot-toast';
import Card from '../components/common/Card';
import {
  Home,
  Target,
  Users,
  Brain,
  Settings,
  BookOpen,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Zap,
  BarChart3,
  Plus,
  Lightbulb,
  ArrowUpRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface QuickAction {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  color: string;
  priority: 'high' | 'medium' | 'low';
}

interface WorkflowStep {
  id: number;
  title: string;
  description: string;
  status: 'completed' | 'current' | 'upcoming';
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface InstructorDashboardStats {
  totalCourses: number;
  totalStudents: number;
  averageProgress: number;
}

const InstructorDashboard: React.FC = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState<CanvasCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [instructorStats, setInstructorStats] = useState<InstructorDashboardStats>({
    totalCourses: 0,
    totalStudents: 0,
    averageProgress: 0,
  });
  const [skillMatricesCount, setSkillMatricesCount] = useState<number>(0);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Load instructor-specific data
      try {
        const coursesResponse = await canvasInstructorAPI.getInstructorCourses();
        setCourses(coursesResponse.data);

        // Try to get skill matrices count from all courses
        let totalMatrices = 0;
        if (coursesResponse.data.length > 0) {
          try {
            const { skillMatrixAPI } = await import('../services/api');
            const matrixPromises = coursesResponse.data.map((course) =>
              skillMatrixAPI.getAllByCourse(course.id).catch(() => ({ data: [] }))
            );
            const matrixResults = await Promise.all(matrixPromises);
            totalMatrices = matrixResults.reduce((acc, result) => acc + result.data.length, 0);
          } catch (error) {
            if (user?.hasCanvasToken) {
              toast.error('Could not load skill matrices count. Data shown may be incomplete.');
            }
            totalMatrices = 0;
          }
        }
        setSkillMatricesCount(totalMatrices);

        // Try to get instructor dashboard data, but don't fail if it's not available
        let actualStudentCount = 0;
        try {
          const dashboardResponse = await instructorAPI.getInstructorDashboard();

          actualStudentCount = dashboardResponse.data.students || 0;

          setInstructorStats({
            totalCourses: coursesResponse.data.length,
            totalStudents: actualStudentCount,
            averageProgress: dashboardResponse.data.averageProgress || 0,
          });
        } catch (error) {
          console.error('Dashboard API error:', error);
          if (user?.hasCanvasToken) {
            toast.error('Could not load instructor dashboard data. Data shown may be incomplete.');
          }

          setInstructorStats({
            totalCourses: coursesResponse.data.length,
            totalStudents: 0,
            averageProgress: 0,
          });
        }
      } catch (error) {
        // If Canvas API fails, still provide useful interface
        console.error('Error loading courses:', error);
        if (user?.hasCanvasToken) {
          toast.error('Could not load courses from Canvas. Please try refreshing.');
        }

        setCourses([]);
        setSkillMatricesCount(0);
        setInstructorStats({
          totalCourses: 0,
          totalStudents: 0,
          averageProgress: 0,
        });
      }
    } catch (error: unknown) {
      console.error('Error loading dashboard data:', error);
      toast.error('Something went wrong loading the dashboard. Please try refreshing.');

      // Provide fallback data so dashboard remains functional
      setCourses([]);

      setInstructorStats({
        totalCourses: 0,
        totalStudents: 0,
        averageProgress: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [user?.hasCanvasToken]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // Enhanced instructor workflow steps with dynamic status
  const workflowSteps: WorkflowStep[] = [
    {
      id: 1,
      title: 'Create Skill Matrix',
      description: 'Define the skills students should master in your course',
      status: skillMatricesCount > 0 ? 'completed' : 'current',
      href: '/skill-matrix',
      icon: Target,
    },
    {
      id: 2,
      title: 'Assign Skills to Questions',
      description: 'Map quiz questions to specific skills using AI assistance',
      status: skillMatricesCount > 0 ? 'current' : 'upcoming',
      href: '/skill-assignment',
      icon: Brain,
    },
    {
      id: 3,
      title: 'Track Student Progress',
      description: 'Monitor individual student skill development in real-time',
      status: 'upcoming',
      href: '/progress',
      icon: BarChart3,
    },
  ];

  // Enhanced quick actions with priorities
  const quickActions: QuickAction[] = [
    {
      title: 'Create Skill Matrix',
      description: 'Define skills and competencies with AI suggestions',
      icon: Target,
      href: '/skill-matrix',
      color: 'bg-blue-500',
      priority: 'high',
    },
    {
      title: 'AI Skill Assignment',
      description: 'Automatically assign skills to quiz questions',
      icon: Brain,
      href: '/skill-assignment',
      color: 'bg-purple-500',
      priority: 'high',
    },
    {
      title: 'Student Progress',
      description: 'View detailed student skill analytics',
      icon: BarChart3,
      href: '/progress',
      color: 'bg-green-500',
      priority: 'medium',
    },
    {
      title: 'Settings',
      description: 'Manage Canvas integration and preferences',
      icon: Settings,
      href: '/settings',
      color: 'bg-ucf-grey',
      priority: 'low',
    },
  ];

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ucf-gold"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Enhanced Welcome Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {getGreeting()}, {user?.name || 'Instructor'}!
            </h1>
            <p className="text-gray-600 mt-2">
              {
                'Transform your assessments into comprehensive skill tracking with AI-powered insights.'
              }
            </p>
          </div>
          <div className="hidden md:flex items-center space-x-3">
            <Link
              to="/skill-matrix"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-ucf-white bg-ucf-gold hover:bg-yellow-600 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Get Started
            </Link>
          </div>
        </div>

        {/* Enhanced Status Indicators */}
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <div className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-current rounded-full mr-2"></div>
              Instructor Dashboard
            </div>
          </div>
          {user?.hasCanvasToken ? (
            <div className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">
              <CheckCircle className="w-3 h-3 mr-1 inline" />
              Canvas Connected
            </div>
          ) : (
            <Link
              to="/settings"
              className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-800/60 transition-colors"
            >
              <AlertTriangle className="w-3 h-3 mr-1 inline" />
              Canvas Setup Required
            </Link>
          )}
          {courses.length > 0 && (
            <div className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300">
              <BookOpen className="w-3 h-3 mr-1 inline" />
              {courses.length} Course{courses.length !== 1 ? 's' : ''} Loaded
            </div>
          )}
        </div>

        {/* Canvas Token Warning for Users Without Token */}
        {!user?.hasCanvasToken && (
          <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/40 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Canvas Integration Required</h3>
                <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                  <p>
                    To access your courses and use AchieveUp features, you need to add your Canvas
                    API token. Go to{' '}
                    <Link to="/settings" className="font-medium underline hover:text-yellow-600 dark:hover:text-yellow-400">
                      Settings
                    </Link>{' '}
                    to configure it.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="text-center hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900/40 rounded-lg mx-auto mb-4">
            <Home className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{courses.length}</div>
          <div className="text-sm text-gray-600">Active Courses</div>
          <div className="text-xs text-gray-500 mt-1">From Canvas LMS</div>
        </Card>

        <Card className="text-center hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900/40 rounded-lg mx-auto mb-4">
            <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{instructorStats.totalStudents}</div>
          <div className="text-sm text-gray-600">Total Students</div>
          <div className="text-xs text-gray-500 mt-1">Across all courses</div>
        </Card>

        <Card className="text-center hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900/40 rounded-lg mx-auto mb-4">
            <Target className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{skillMatricesCount}</div>
          <div className="text-sm text-gray-600">Skill Matrices</div>
          <div className="text-xs text-gray-500 mt-1">
            {skillMatricesCount === 0 ? 'Ready to create' : 'Across all courses'}
          </div>
        </Card>

        <Card className="text-center hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 dark:bg-yellow-900/40 rounded-lg mx-auto mb-4">
            <Brain className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">AI</div>
          <div className="text-sm text-gray-600">Powered</div>
          <div className="text-xs text-gray-500 mt-1">Smart suggestions</div>
        </Card>
      </div>

      {/* Instructor-specific Workflow Guide */}
      <Card title="AchieveUp Workflow" className="mb-8">
        <div className="space-y-6">
          <p className="text-gray-600">
            Set up skill tracking for your courses in three simple steps:
          </p>

          <div className="space-y-4">
            {workflowSteps.map((step, index) => {
              const Icon = step.icon;
              const isLast = index === workflowSteps.length - 1;

              return (
                <div key={step.id} className="relative">
                  <div
                    className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                      step.status === 'current'
                        ? 'border-ucf-gold bg-yellow-50 dark:bg-yellow-900/40'
                        : step.status === 'completed'
                          ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/40'
                          : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                        step.status === 'current'
                          ? 'bg-ucf-gold text-ucf-white'
                          : step.status === 'completed'
                            ? 'bg-green-500 text-ucf-white'
                            : 'bg-gray-300 text-gray-600'
                      }`}
                    >
                      {step.status === 'completed' ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <Icon className="w-5 h-5" />
                      )}
                    </div>

                    <div className="flex-1">
                      <h3
                        className={`font-medium ${
                          step.status === 'current'
                            ? 'text-yellow-900 dark:text-yellow-200'
                            : step.status === 'completed'
                              ? 'text-green-900 dark:text-green-200'
                              : 'text-gray-700'
                        }`}
                      >
                        Step {step.id}: {step.title}
                      </h3>
                      <p
                        className={`text-sm ${
                          step.status === 'current'
                            ? 'text-yellow-700 dark:text-yellow-300'
                            : step.status === 'completed'
                              ? 'text-green-700 dark:text-green-300'
                              : 'text-gray-600'
                        }`}
                      >
                        {step.description}
                      </p>
                    </div>

                    {step.status === 'current' && (
                      <Link
                        to={step.href}
                        className="flex-shrink-0 ml-4 inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded text-ucf-gold bg-yellow-100 dark:bg-yellow-900/40 hover:bg-yellow-200 dark:hover:bg-yellow-800/60 transition-colors"
                      >
                        Start
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    )}
                  </div>

                  {!isLast && (
                    <div
                      className={`absolute left-9 top-16 w-0.5 h-6 ${
                        step.status === 'completed' ? 'bg-green-300' : 'bg-gray-200'
                      }`}
                    ></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Enhanced Quick Actions */}
      <Card
        title="Essential Tools"
        className="mb-8"
        headerActions={
          <Link
            to="/skill-matrix"
            className="text-sm text-ucf-gold hover:text-yellow-700 dark:hover:text-yellow-300 font-medium"
          >
            Get Started <ArrowUpRight className="w-4 h-4 inline ml-1" />
          </Link>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <Link
                key={index}
                to={action.href}
                className="group block p-6 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-lg transition-all duration-200"
              >
                <div className="text-center">
                  <div
                    className={`w-12 h-12 ${action.color} rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-200`}
                  >
                    <Icon className="w-6 h-6 text-ucf-white" />
                  </div>
                  <h3 className="font-medium text-gray-900 group-hover:text-gray-700 mb-2">
                    {action.title}
                  </h3>
                  <p className="text-sm text-gray-600 group-hover:text-gray-500">
                    {action.description}
                  </p>
                  {action.priority === 'high' && (
                    <div className="mt-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        <Zap className="w-3 h-3 mr-1" />
                        Recommended
                      </span>
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </Card>

      {/* Course Overview / Tips */}
      <Card title={courses.length > 0 ? 'Your Courses' : 'Getting Started Tips'} className="h-fit">
        {courses.length > 0 ? (
          <div className="space-y-3">
            {courses.slice(0, 5).map((course, index) => (
              <div
                key={course.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 truncate">{course.name}</p>
                    <p className="text-xs text-gray-600">{course.code}</p>
                  </div>
                </div>
                <Link
                  to={`/skill-matrix?course=${course.id}`}
                  className="text-xs text-ucf-gold hover:text-yellow-700 dark:hover:text-yellow-300 font-medium"
                >
                  Setup
                </Link>
              </div>
            ))}
            {courses.length > 5 && (
              <p className="text-xs text-gray-500 text-center pt-2">
                +{courses.length - 5} more courses
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Lightbulb className="w-5 h-5 text-yellow-500 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">Set up Canvas Integration</p>
                <p className="text-xs text-gray-600">
                  Connect your Canvas account to load courses automatically
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Lightbulb className="w-5 h-5 text-yellow-500 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">Start with One Course</p>
                <p className="text-xs text-gray-600">
                  Choose your most important course and create a skill matrix first
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Lightbulb className="w-5 h-5 text-yellow-500 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">Use AI Suggestions</p>
                <p className="text-xs text-gray-600">
                  Let our AI recommend skills and map them to your quiz questions
                </p>
              </div>
            </div>
            <div className="pt-3">
              <Link
                to="/settings"
                className="inline-flex items-center text-sm text-ucf-gold hover:text-yellow-700 dark:hover:text-yellow-300 font-medium"
              >
                Configure Canvas <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};
export default InstructorDashboard;
